import { useEffect, useState, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';

const INITIAL_PARAMS = { limit: 50, offset: 0, query: '' };

const useAsyncPaginatedSelect = ({
  additionalRequestParams,
  clearEntitiesAction,
  defaultOption,
  entitiesFetchingSucceedSelector,
  entitiesIsLastPageSelector,
  entitiesLoadingSelector,
  entitiesSelector,
  mapToOptionsFn,
  sendAction,
}) => {
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useState(INITIAL_PARAMS);
  const [hasEntities, setHasEntities] = useState(false);

  const entities = useSelector(entitiesSelector);

  const isLoading = useSelector(entitiesLoadingSelector);

  const isLastPage = useSelector(entitiesIsLastPageSelector);

  const isFetchingSucceed = useSelector(entitiesFetchingSucceedSelector);

  useEffect(() => {
    if (entities.length) {
      setHasEntities(true);
    }
  }, [entities]);

  const shouldPreselectDefaultOption = useMemo(
    () =>
      Boolean(
        !hasEntities &&
          entities.length === 0 &&
          searchParams.query === INITIAL_PARAMS.query &&
          !isLoading &&
          isFetchingSucceed,
      ),
    [searchParams, entities, isLoading, isFetchingSucceed, hasEntities],
  );

  const fetchEntities = useCallback(
    params => {
      if (params.offset === 0) {
        dispatch(clearEntitiesAction());
      }

      dispatch(
        sendAction({
          ...params,
          ...additionalRequestParams,
        }),
      );
    },
    [dispatch, sendAction, clearEntitiesAction, additionalRequestParams],
  );

  const convertedEntities = entities.map(mapToOptionsFn);

  const debouncedFetchEntities = useMemo(() => debounce(fetchEntities, 500), [
    fetchEntities,
  ]);

  const handleInputChange = (value, { action } = {}) => {
    if (action !== 'input-change') return;

    const params = { ...searchParams, query: value, offset: 0 };

    setSearchParams(params);

    debouncedFetchEntities.cancel();
    debouncedFetchEntities(params);
  };

  const handleMenuScrollToBottom = () => {
    if (isLastPage || isLoading) return;

    const params = {
      ...searchParams,
      offset: searchParams.offset + searchParams.limit,
    };

    setSearchParams(params);

    debouncedFetchEntities(params);
  };

  useEffect(() => {
    return () => {
      debouncedFetchEntities.cancel();
    };
  }, [debouncedFetchEntities]);

  useEffect(() => {
    fetchEntities(INITIAL_PARAMS);

    return () => {
      dispatch(clearEntitiesAction());
    };
  }, []);

  return {
    isLoading,
    handleMenuScrollToBottom,
    handleInputChange,
    entities:
      defaultOption != null
        ? [defaultOption, ...convertedEntities]
        : convertedEntities,
    shouldPreselectDefaultOption,
  };
};

export default useAsyncPaginatedSelect;



// WEBPACK FOOTER //
// ./src/hooks/useAsyncPaginatedSelect.js